import type { BuyerIdentity } from "./wallet-api";
import type { CoreContext } from "./context";
import type { CodeOutcome, CoreState } from "./state";
import { isZeroTotal } from "./state";

/**
 * Who is buying: attesting the buyer on the session with a verified token,
 * asking for a code when there is none, and letting a Lissen buyer go.
 */
export function createIdentityFlows(ctx: CoreContext) {
  const { deps, config, set, sessionRead, loaded, refs } = ctx;
  const { buyer, wallet } = deps.engine;
  const { broker } = deps.identity;
  const { sessionId } = config;

  /** Attest the buyer with a verified action token, then re-read the cart
   *  the engine re-quoted for them. */
  const attestWith = async (
    token: string,
    options: { refreshInBackground?: boolean } = {},
  ): Promise<BuyerIdentity | null> => {
    if (!wallet) return null;
    const result = await wallet.identity(sessionId, { action_token: token });
    // The action token is consumed once identity succeeds. Keep that result
    // even if the separate read of the re-quoted cart is unavailable.
    set((s) => ({
      attested: result,
      // The first attestation chooses the buyer's newest saved card; a
      // later one (a new card's consent at pay, a renewed proof) leaves
      // their choice as it is, so the card being typed stays the one that
      // pays.
      selected:
        s.attested &&
        (s.selected === null ||
          result.saved_cards.some((card) => card.id === s.selected))
          ? s.selected
          : (result.saved_cards[0]?.id ?? null),
      ...(result.email
        ? { email: { value: result.email, chip: true, source: "lissen" } }
        : { email: s.email }),
    }));
    if (buyer) {
      // The host's token is asked for as the checkout starts, so this can
      // land before the first read of the session has: the re-quoted cart
      // is read once that has, never raced against it.
      const refresh = loaded
        .then(async () => {
          const apply = sessionRead();
          const refreshed = await buyer.get(sessionId);
          if (!refreshed.data) throw new Error("Checkout refresh failed");
          apply({ type: "session_updated", session: refreshed.data });
        })
        .catch(() =>
          set({
            notice:
              "Your email is verified, but we couldn't refresh your checkout. Reload the page to see your latest pricing.",
          }),
        );
      // Host mode's silent attestation: the re-quoted cart follows on its
      // own rather than holding the pay button for another round trip.
      if (!options.refreshInBackground) await refresh;
    }
    return result;
  };

  /** Ask the broker for a code and remember why. Resolves true while a code
   *  is pending; a recognised browser is attested at once instead. */
  const startCode = async (
    view: CoreState,
    request: { email?: string; reverify?: boolean; consents?: Array<string> },
    why: CodeOutcome,
  ) => {
    if (!broker || !view.identity.connected) return false;
    set({ notice: null });
    try {
      const answer = await deps.identity.checkoutToken({
        sessionId,
        rememberMe: true,
        consents: request.consents ?? [],
        ...(request.email ? { email: request.email } : {}),
        ...(request.reverify ? { reverify: true } : {}),
      });
      set({ outcome: why });
      if ("token" in answer) {
        if (isZeroTotal(view) && why.kind === "remember")
          set({ remembered: "email" });
        else await attestWith(answer.token);
        return false;
      }
      return true;
    } catch {
      set({ notice: "We couldn't send a code. Try again." });
      return false;
    }
  };

  /** The × on the chip. For a Lissen buyer it is the sign-out: the browser
   *  session is revoked on the broker, so the next checkout — and a
   *  different address — start as a stranger. Dismissing the chip alone left
   *  the session in place, and a buyer who wanted out could not get out. */
  const removeEmail = async (view: CoreState) => {
    const wasLissen = view.email.source === "lissen";
    if (wasLissen && broker && view.identity.connected) {
      set({ busy: "checking" });
      try {
        await broker.forget();
        // The engine's session must forget too: an attestation left on
        // the cart would still decide whose wallet the next card joins.
        if (view.attested && wallet && buyer) {
          await wallet.clearIdentity(sessionId);
          const apply = sessionRead();
          const refreshed = await buyer.get(sessionId);
          if (refreshed.data)
            apply({ type: "session_updated", session: refreshed.data });
        }
      } catch {
        set({ notice: "We couldn't sign you out of Lissen. Try again." });
        return;
      } finally {
        set({ busy: null });
      }
    }
    refs.dismissed = true;
    set({
      email: { value: "", chip: false, source: "typed" },
      attested: null,
      selected: null,
    });
    refs.enrolmentFor = null;
    if (broker && view.identity.connected) void broker.cancelCode();
    if (wasLissen) set({ remember: true });
  };

  return { attestWith, startCode, removeEmail };
}

export type IdentityFlows = ReturnType<typeof createIdentityFlows>;
